import { useMemo } from 'react'
import type { Abi, Address as AddressType } from 'viem'
import {
  BaseError,
  ContractFunctionExecutionError,
  ContractFunctionRevertedError,
  getContract as getContractViem,
} from 'viem'

import { clientWallet, publicWallet } from 'libs/viem/config'
import { logError } from 'utils/logger'

export const getContract = ({
  contractAddress,
  ABI,
}: {
  contractAddress: AddressType
  ABI: any
}) => {
  return getContractViem({
    address: contractAddress,
    abi: ABI as unknown as Abi,
    client: { public: publicWallet, wallet: clientWallet },
  }) as unknown as any
}

export const useContractViem = (contractAddress: AddressType | null, ABI: any) => {
  return useMemo(() => {
    if (!contractAddress || !ABI) return null
    try {
      return getContract({ contractAddress, ABI })
    } catch (error) {
      logError('Failed to get contract', error)
      return null
    }
  }, [contractAddress, ABI])
}

/**
 * Send a simulated request with the connected wallet and wait for 1 confirmation.
 * Throws when the receipt status is not success.
 */
export const writeContractAndWait = async (request: any) => {
  const hash = await clientWallet.writeContract(request)
  const receipt = await publicWallet.waitForTransactionReceipt({ hash, confirmations: 1 })
  if (receipt.status !== 'success') {
    throw new Error(`Transaction ${hash} reverted`)
  }
  return { hash, receipt }
}

export const customErrorContract = (error: any) => {
  if (error instanceof ContractFunctionExecutionError) {
    return error.shortMessage
  } else if (error instanceof BaseError) {
    const revertError = error.walk((err) => err instanceof ContractFunctionRevertedError)
    if (revertError instanceof ContractFunctionRevertedError) {
      const errorName = revertError.data?.errorName ?? 'Failed to get contract'
      return errorName
    }
  } else {
    return error !== null && error !== undefined && error !== ''
      ? error?.message || 'Failed to get contract'
      : 'Failed to get contract'
  }
}
